import { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import {
  canSeek,
  currentTrack,
  durationOf,
  initialPlayerState,
  reducer,
  type Action,
  type PlayerState,
  type Track,
} from './queue';

/**
 * The player provider, and the `<audio>` element it drives.
 *
 * ## Why the state lives here and not in a route
 *
 * "Persistent playback across navigation" is the requirement, and the only way to get it is for
 * the player to sit **outside** the router. So the provider wraps the whole app, the element is
 * created once for the lifetime of the page, and navigating changes what the app renders without
 * touching the element. That is also why the queue is a reducer rather than component state: a
 * remount must not be able to drop it.
 *
 * ## Why the content URL is a function and not a string
 *
 * The handoff is explicit that playback URLs must not be hard-coded: media resolves through the
 * canonical layer. The player therefore asks its host for a URL per track id, so pointing it at
 * the Chain Audio API is a host decision and the component stays free of one.
 *
 * ## Why the resolver is held in a ref
 *
 * The obvious `useEffect(..., [contentUrl])` is a trap: every caller passes an inline arrow, so
 * the identity changes on every render and the effect re-fires — re-issuing `play()` on every
 * state update. That is a player that stutters or restarts itself under load, and it is invisible
 * until you look for it. The ref keeps the effect's dependencies to the two things that actually
 * should trigger a load: which track, and whether it is playing.
 */

export type ContentUrlResolver = (track: Track) => string;

interface PlayerContextValue {
  state: PlayerState;
  dispatch: React.Dispatch<Action>;
  /** The current track, for convenience. */
  track: Track | undefined;
  canSeek: boolean;
  /** The duration the transport scrubs against, from whichever source knows it. */
  durationMs: number;
  /** 0–1, for a progress bar. */
  progress: number;
  /** The element, for anything the component does not wrap. */
  audioRef: React.RefObject<HTMLAudioElement>;
}

const PlayerContext = createContext<PlayerContextValue | null>(null);

export function usePlayer(): PlayerContextValue {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used inside <PlayerProvider>');
  return ctx;
}

export interface PlayerProviderProps {
  children: React.ReactNode;
  /** Resolves a track to a content URL. See the note above about hard-coded URLs. */
  contentUrl: ContentUrlResolver;
}

export function PlayerProvider({ children, contentUrl }: PlayerProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialPlayerState);
  const audioRef = useRef<HTMLAudioElement>(null);
  const track = currentTrack(state);

  // Held in a ref so a caller's inline arrow cannot drive the load effect. See the module note.
  const contentUrlRef = useRef(contentUrl);
  contentUrlRef.current = contentUrl;

  // Keep the element's volume and mute in step. Volume is applied even when muted so unmuting
  // returns to the chosen level rather than to silence.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.volume = state.volume;
    el.muted = state.muted;
  }, [state.volume, state.muted]);

  // Load and play when the track changes. `kind` is respected: a module has no playable URL and
  // is never handed to the element, so a MIDI record cannot cause a failed request.
  //
  // Dependencies are deliberately just the track id and the play flag — the resolver is reached
  // through a ref, so a new function identity cannot re-trigger a load.
  //
  // **`src` is set and `load()` is called, but `play()` is NOT called here.** Calling it straight
  // after `load()` races the media element: the promise rejects with an AbortError because there is
  // no data yet, and the rejection handler then pauses — leaving a track that looks queued, seeks
  // correctly, and never plays. That is exactly what happened in the browser. Playback is started
  // by `onCanPlay` below, once the element can actually begin.
  const trackId = track?.id;
  const trackKind = track?.kind;
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !trackId || !trackKind) return;

    if (trackKind !== 'waveform') {
      el.removeAttribute('src');
      el.dataset.trackId = '';
      el.load();
      return;
    }

    if (el.dataset.trackId !== trackId) {
      el.dataset.trackId = trackId;
      el.src = contentUrlRef.current({ id: trackId } as Track);
      el.load();
    }

    if (!state.playing) {
      el.pause();
    } else if (el.readyState >= 3) {
      // Enough data is buffered to start; no need to wait for an event that already fired.
      void el.play().catch(() => undefined);
    }
    // Otherwise `onCanPlay` starts it. An AbortError from a superseded load is not a failure and
    // must not be recorded as one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackId, trackKind, state.playing]);

  const currentDuration = durationOf(state, track);

  // Seeking: only applied when the state's position diverges from the element's, so a normal
  // progress tick does not fight itself.
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !canSeek(state)) return;
    const elementMs = el.currentTime * 1000;
    if (Math.abs(elementMs - state.positionMs) > 750) {
      el.currentTime = state.positionMs / 1000;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.positionMs]);

  const progress = currentDuration > 0 ? state.positionMs / currentDuration : 0;

  const value = useMemo<PlayerContextValue>(
    () => ({
      state,
      dispatch,
      track,
      canSeek: canSeek(state),
      durationMs: currentDuration,
      progress,
      audioRef,
    }),
    [state, track, currentDuration, progress],
  );

  return (
    <PlayerContext.Provider value={value}>
      {children}
      {/*
        The element is created once, at the provider, and never unmounts. Preload is `metadata`
        so a large inscription is not downloaded just because it is queued — the bytes are pulled
        when the user actually plays, and Range requests do the seeking.
      */}
      <audio
        ref={audioRef}
        preload="metadata"
        // Playback begins here, not in the effect: `canplay` is the first moment the element can
        // actually start, and play() before it races the load. It also fires on every later
        // readiness, so a resumed or re-buffered track starts without another dispatch.
        onCanPlay={(e) => {
          if (state.playing) {
            void e.currentTarget.play().catch(() => undefined);
          }
        }}
        onTimeUpdate={(e) =>
          dispatch({ type: 'tick', positionMs: e.currentTarget.currentTime * 1000 })
        }
        onLoadedMetadata={(e) => {
          const el = e.currentTarget;
          const elementMs = isFinite(el.duration) ? el.duration * 1000 : undefined;
          dispatch({
            type: 'tick',
            positionMs: el.currentTime * 1000,
            durationMs: elementMs,
          });
        }}
        onEnded={() => dispatch({ type: 'ended' })}
        onError={() => {
          // A failed item is recorded and stepped past, never allowed to stop the queue. An
          // AbortError from a superseded src is not surfaced here at all — it is not an error
          // event — so anything reaching this handler is a real failure to fetch or decode.
          const id = audioRef.current?.dataset.trackId;
          if (id) dispatch({ type: 'failed', id });
        }}
      />
    </PlayerContext.Provider>
  );
}
